import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from './../general/AuthContext';
import { User } from 'lucide-react';
import '../styles/staff/Staff.css';

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const fmt = (v) => (v === null ? '—' : v.toLocaleString());

/** Hero stat block (white-on-gradient). */
function HeroStat({ label, value, sub }) {
    return (
        <div style={{ minWidth: 86 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'white', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                {value}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 3 }}>
                {label}
            </div>
            {sub && <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{sub}</div>}
        </div>
    );
}

function EmployeeDetails() {
    const { id } = useParams();
    const [employeeDetails, setEmployeeDetails] = useState(null);
    const [metrics, setMetrics] = useState({});
    const { auth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!auth.isAuthenticated) { navigate('/login'); return; }
        fetchEmployeeDetails();
    }, [auth, navigate, id]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchEmployeeDetails = async () => {
        try {
            const response = await fetch(`/api/employee/${id}`, {
                headers: { Authorization: `Bearer ${auth.token}` },
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setEmployeeDetails(data);
            const ids = (Array.isArray(data) ? data : []).map((r) => r.ID).filter(Boolean);
            if (ids.length) {
                const m = await fetch(`/api/unit-metrics?ids=${ids.join(',')}`, {
                    headers: { Authorization: `Bearer ${auth.token}` },
                });
                setMetrics(m.ok ? await m.json() : {});
            }
        } catch (error) {
            console.error('Error fetching employee details:', error);
            navigate('/login');
        }
    };

    if (!employeeDetails) {
        return (
            <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>
                Loading…
            </div>
        );
    }

    if (employeeDetails.length === 0) {
        return (
            <div className="card card-pad" style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 14, color: 'var(--fg-2)', marginBottom: 12 }}>No units assigned to this employee.</div>
                <Link to="/staff" className="btn btn-secondary">Return to Staff</Link>
            </div>
        );
    }

    const emp = employeeDetails[0];

    // ── The overall picture of what this employee manages ──────────────────
    const towns = new Set(employeeDetails.map((r) => (r.Town || '').trim()).filter(Boolean));
    let activeJobs = 0, hasJobs = false;
    let duesPayers = 0, hasDues = false;
    let members = 0, hasMembers = false;
    employeeDetails.forEach((r) => {
        const m = metrics[r.ID] || {};
        const aj = num(m.ActiveJobCount);
        const dp = num(m.DuesPayers);
        const tm = num(m.TotalMembers);
        if (aj !== null) { activeJobs += aj; hasJobs = true; }
        if (dp !== null) { duesPayers += dp; hasDues = true; }
        if (tm !== null) { members += tm; hasMembers = true; }
    });
    const pctPaying = hasDues && hasMembers && members > 0
        ? `${((duesPayers / members) * 100).toFixed(1)}%`
        : null;

    return (
        <>
            {/* Profile hero — the employee's book of business at a glance */}
            <div className="list-toolbar" style={{
                background: 'linear-gradient(135deg, var(--accent) 0%, #0093CB 100%)',
                borderRadius: 'var(--radius-lg)',
                padding: '24px 28px',
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 24,
                flexWrap: 'wrap',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, minWidth: 220 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <User size={28} color="white" />
                    </div>
                    <div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>
                            {emp.employee_full_name}
                        </div>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 3, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                            {emp.role_title || emp.rep_type || 'Staff Member'}
                        </div>
                    </div>
                </div>
                <div style={{
                    display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center',
                    marginLeft: 'auto', paddingLeft: 8,
                }}>
                    <HeroStat label="Units" value={employeeDetails.length} />
                    <HeroStat label="Towns" value={towns.size} />
                    <HeroStat label="Active Jobs" value={hasJobs ? fmt(activeJobs) : '—'} />
                    <HeroStat label="Dues Payers" value={hasDues ? fmt(duesPayers) : '—'} />
                    <HeroStat label="Members" value={hasMembers ? fmt(members) : '—'} />
                    {pctPaying && <HeroStat label="% Paying" value={pctPaying} />}
                </div>
            </div>

            {/* Units table */}
            <div className="card">
                <div className="card-head list-toolbar">
                    <h3>Assigned Bargaining Units</h3>
                    <span className="badge" style={{ marginLeft: 8 }}>{employeeDetails.length} units</span>
                </div>
                <div className="table-wrap">
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th>Local</th>
                                <th>Chapter</th>
                                <th>Bargaining Unit</th>
                                <th>Town</th>
                                <th style={{ textAlign: 'right' }}>Active Jobs</th>
                                <th style={{ textAlign: 'right' }}>Dues Payers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeDetails.map((row, index) => {
                                const m = metrics[row.ID] || {};
                                return (
                                    <tr key={index}>
                                        <td data-label="Local" style={{ whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                                            {row.UnitNumber}
                                        </td>
                                        <td data-label="Chapter" style={{ whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                                            {row.UnitChapter != null ? row.UnitChapter.toString().padStart(4, '0') : '—'}
                                        </td>
                                        <td className="card-title">
                                            <Link to={`/unit-info/${row.ID}?emp=${id}`} style={{ color: 'var(--fg-link)', textDecoration: 'none' }}>
                                                {row.UnitName}
                                            </Link>
                                        </td>
                                        <td data-label="Town">
                                            {row.Town
                                                ? <Link to={`/town/${row.Town}`} style={{ color: 'var(--fg-link)', textDecoration: 'none' }}>{row.Town}</Link>
                                                : '—'}
                                        </td>
                                        <td data-label="Active Jobs" style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                                            {fmt(num(m.ActiveJobCount))}
                                        </td>
                                        <td data-label="Dues Payers" style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                                            {fmt(num(m.DuesPayers))}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

export default EmployeeDetails;
